<template>
  <div>
    <!-- Trigger button (used wherever the component is placed) -->
    <v-btn color="secondary" variant="outlined" @click="open()">
      <v-icon start>{{ mdiCreationOutline }}</v-icon>
      AI Setup
    </v-btn>

    <!-- Fixed side panel — sits on top of page content, does not block it -->
    <teleport to="body">
      <transition name="nl-slide">
        <div v-if="visible" class="nl-panel" :class="{ 'nl-panel--minimized': minimized }">

          <!-- Header bar -->
          <div class="nl-panel__header">
            <span class="nl-panel__title">WhaleTrader AI (BETA)</span>
            <span v-if="dailyLimit" class="nl-panel__quota">
              {{ queriesUsed }}/{{ dailyLimit }} today
            </span>
            <v-btn
              :icon="minimized ? mdiChevronLeft : mdiChevronRight"
              size="x-small"
              variant="text"
              color="white"
              :title="minimized ? 'Expand' : 'Minimize'"
              @click="minimized = !minimized"
            />
            <v-btn
              :icon="mdiClose"
              size="x-small"
              variant="text"
              color="white"
              title="Close"
              @click="close()"
            />
          </div>

          <!-- Body — hidden when minimized -->
          <div v-show="!minimized" class="nl-panel__body">

            <!-- Conversation -->
            <div ref="chatScroll" class="nl-panel__chat">
              <div v-if="messages.length === 0" class="nl-message nl-message--assistant">
                <div class="nl-bubble">
                  Describe your strategy in plain English:
                  <ul class="mt-1 mb-0 pl-4">
                    <li><em>"Top hedge funds, insider confirmation, large cap, 8% stop loss"</em></li>
                    <li><em>"Activist 13D with 25% profit target"</em></li>
                    <li><em>"13F filtered by uptrend above 200-day MA"</em></li>
                  </ul>
                  <div class="mt-2 text-caption opacity-70">
                    Changes apply to the open model without closing this panel.
                  </div>
                </div>
              </div>

              <template v-for="(msg, i) in messages" :key="i">
                <div :class="['nl-message', msg.role === 'user' ? 'nl-message--user' : 'nl-message--assistant']">
                  <div class="nl-bubble">
                    <!-- eslint-disable-next-line vue/no-v-html -->
                    <span v-html="formatMessage(msg.content)" />
                  </div>
                </div>

                <div v-if="msg.role === 'assistant' && msg.delta && hasDelta(msg.delta)" class="nl-delta-card">
                  <div class="nl-delta-lines">
                    <div v-for="line in summarizeDelta(msg.delta)" :key="line" class="nl-delta-line">
                      {{ line }}
                    </div>
                  </div>
                  <div class="nl-delta-actions">
                    <v-btn
                      size="x-small"
                      color="primary"
                      variant="flat"
                      :disabled="msg.applied"
                      @click="applyDelta(msg, msg.delta)"
                    >
                      {{ msg.applied ? '✓ Applied' : 'Apply' }}
                    </v-btn>
                  </div>
                </div>
              </template>

              <div v-if="loading" class="nl-message nl-message--assistant">
                <div class="nl-bubble">
                  <v-progress-circular indeterminate size="12" width="2" class="mr-1" />
                  Thinking...
                </div>
              </div>
            </div>

            <!-- Input -->
            <div class="nl-panel__input">
              <v-textarea
                v-model="inputText"
                :disabled="loading"
                placeholder="Describe or modify strategy..."
                rows="2"
                auto-grow
                max-rows="5"
                variant="outlined"
                density="compact"
                hide-details
                @keydown.enter.exact.prevent="submit()"
              />
              <div class="d-flex justify-space-between align-center mt-1">
                <v-btn
                  size="x-small"
                  variant="text"
                  :disabled="loading || messages.length === 0"
                  @click="reset()"
                >
                  <v-icon start size="12">{{ mdiRestart }}</v-icon>
                  Clear
                </v-btn>
                <v-btn
                  color="primary"
                  size="small"
                  :disabled="loading || !inputText.trim()"
                  :loading="loading"
                  @click="submit()"
                >
                  <v-icon start>{{ mdiSend }}</v-icon>
                  Send
                </v-btn>
              </div>
            </div>
          </div>

        </div>
      </transition>
    </teleport>
  </div>
</template>

<script>
import axios from 'axios'
import {
  mdiCreationOutline,
  mdiClose,
  mdiSend,
  mdiRestart,
  mdiChevronLeft,
  mdiChevronRight
} from '@mdi/js'
import { useAppStore } from '@/store/app'

export default {
  name: 'NlInput',

  emits: ['apply-criteria'],

  data() {
    return {
      mdiCreationOutline,
      mdiClose,
      mdiSend,
      mdiRestart,
      mdiChevronLeft,
      mdiChevronRight,

      visible:    false,
      minimized:  false,
      loading:    false,
      inputText:  '',
      queriesUsed: 0,
      dailyLimit:  null,

      messages:            [],
      conversationHistory: []
    }
  },

  methods: {
    open() {
      this.visible   = true
      this.minimized = false
    },

    close() {
      this.visible = false
    },

    async submit() {
      const query = this.inputText.trim()
      if (!query || this.loading) return

      this.messages.push({ role: 'user', content: query, delta: null })
      this.inputText = ''
      this.loading   = true
      await this.$nextTick()
      this.scrollToBottom()

      try {
        const currentCriteria = useAppStore().whaleTraderCriteria
        const response = await axios.post('/dashboard/parse_whale_trader_nl', {
          query,
          conversation_history: this.conversationHistory,
          current_criteria:     currentCriteria
        })

        const { criteria_delta, explanation, conversation_history, queries_used, daily_limit } = response.data

        if (queries_used !== undefined) this.queriesUsed = queries_used
        if (daily_limit  !== undefined) this.dailyLimit  = daily_limit

        this.conversationHistory = conversation_history || []
        this.messages.push({
          role:    'assistant',
          content: explanation || 'Done — see configuration below.',
          delta:   criteria_delta || null,
          applied: false
        })
      } catch (err) {
        const status = err.response?.status
        const data   = err.response?.data || {}

        if (status === 429) {
          if (data.daily_limit)        this.dailyLimit  = data.daily_limit
          if (data.queries_used != null) this.queriesUsed = data.queries_used
        }

        const msg = data.error || err.message
        useAppStore().showNotification({ type: 'error', text: msg })
        this.messages.push({
          role:    'assistant',
          content: status === 429
            ? `Daily limit reached (${data.daily_limit || '?'}/day). Upgrade for more queries.`
            : `Error: ${msg}`,
          delta:   null,
          applied: false
        })
      } finally {
        this.loading = false
        await this.$nextTick()
        this.scrollToBottom()
      }
    },

    applyDelta(msg, delta) {
      this.$emit('apply-criteria', delta)
      msg.applied = true
    },

    reset() {
      this.messages            = []
      this.conversationHistory = []
      this.inputText           = ''
    },

    scrollToBottom() {
      const el = this.$refs.chatScroll
      if (el) el.scrollTop = el.scrollHeight
    },

    hasDelta(delta) {
      return delta && Object.keys(delta).length > 0
    },

    formatMessage(text) {
      if (!text || typeof text !== 'string') return ''
      return text
        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
        .replace(/\n/g, '<br>')
    },

    summarizeDelta(delta) {
      const lines = []
      const signals = delta?.signals || {}
      const signalNames = {
        f13_signal:           '13F Signal',
        d13_signal:           '13D/G Signal',
        insider_signal:       'Insider Signal',
        trend_signal:         'Trend Filter',
        whale_ml_signal:      'WhaleML',
        fundamental_signal:   'Fundamental Filter',
        f13_indicator_signal: 'F13 Indicator'
      }
      for (const [key, cfg] of Object.entries(signals)) {
        const name = signalNames[key] || key
        if (cfg.enabled === false) {
          lines.push(`• ${name}: off`)
        } else if (cfg.enabled) {
          const parts = []
          if (cfg.join_type)                    parts.push(cfg.join_type)
          if (cfg.minimum_funds)                parts.push(`min ${cfg.minimum_funds} funds`)
          if (cfg.only_include_if_qoq_increase) parts.push('QoQ↑ only')
          if (cfg.whalescore)                   parts.push(`score≥${cfg.whalescore}`)
          if (cfg.filer_performance?.selected)   parts.push(`top ${cfg.filer_performance.max_filers} funds (${cfg.filer_performance.top_performing_type?.description || 'dynamic'})`)
          if (cfg.filer_performance?.fund_classifications?.length) parts.push(cfg.filer_performance.fund_classifications.map(c => c.description).join('/'))
          if (cfg.minimum_unique_owners)        parts.push(`${cfg.minimum_unique_owners}+ insiders`)
          if (cfg.include_13d && !cfg.include_13g) parts.push('activist only')
          if (cfg.min_score)                    parts.push(`ML≥${cfg.min_score}`)
          if (cfg.number_of_days_to_evaluate)   parts.push(`active ${cfg.number_of_days_to_evaluate}d`)
          lines.push(`• ${name}${parts.length ? ': ' + parts.join(', ') : ''}`)
        }
      }
      const exits = delta?.exit_rules?.long || {}
      if (exits.stop_loss_exit_enabled && exits.stop_loss_exit) lines.push(`• Stop loss: ${exits.stop_loss_exit}%`)
      if (exits.stop_gain_exit_enabled && exits.stop_gain_exit) lines.push(`• Profit target: ${exits.stop_gain_exit}%`)
      if (exits.exit_max_time_enabled  && exits.exit_max_time)  lines.push(`• Max hold: ${exits.exit_max_time}d`)
      const entry = delta?.entry_rules?.long || {}
      if (entry.percent_of_portfolio) lines.push(`• Position size: ${entry.percent_of_portfolio}%`)
      const filters = delta?.filters || {}
      if (filters.market_caps?.length) lines.push(`• Caps: ${filters.market_caps.map(c => c.name).join(', ')}`)
      const risk = delta?.risk_management || {}
      if (risk.max_individual) lines.push(`• Max position: ${risk.max_individual}%`)
      const general = delta?.general || {}
      if (general.start_date) lines.push(`• Start: ${general.start_date}`)
      return lines.length ? lines : ['No changes detected']
    }
  }
}
</script>

<style scoped>
/* ── Panel container ─────────────────────────────────────────────────── */
.nl-panel {
  position: fixed;
  top: 80px;
  right: 0;
  width: 340px;
  max-height: calc(100vh - 100px);
  display: flex;
  flex-direction: column;
  background: rgb(var(--v-theme-surface));
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-right: none;
  border-radius: 8px 0 0 8px;
  box-shadow: -4px 4px 16px rgba(0, 0, 0, 0.2);
  z-index: 200;
  overflow: hidden;
}

.nl-panel--minimized {
  width: 140px;
}

/* ── Header ──────────────────────────────────────────────────────────── */
.nl-panel__header {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 8px;
  background: rgb(var(--v-theme-primary));
  color: white;
  flex-shrink: 0;
  cursor: default;
  user-select: none;
}

.nl-panel__title {
  font-size: 0.8rem;
  font-weight: 600;
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.nl-panel__quota {
  font-size: 0.7rem;
  opacity: 0.8;
  white-space: nowrap;
}

/* ── Body ────────────────────────────────────────────────────────────── */
.nl-panel__body {
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow: hidden;
}

/* ── Chat scroll area ────────────────────────────────────────────────── */
.nl-panel__chat {
  flex: 1;
  overflow-y: auto;
  padding: 10px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-height: 200px;
  max-height: calc(100vh - 340px);
}

/* ── Input area ──────────────────────────────────────────────────────── */
.nl-panel__input {
  padding: 8px;
  border-top: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  flex-shrink: 0;
}

/* ── Messages ────────────────────────────────────────────────────────── */
.nl-message {
  display: flex;
}
.nl-message--user      { justify-content: flex-end; }
.nl-message--assistant { justify-content: flex-start; }

.nl-bubble {
  max-width: 90%;
  padding: 6px 10px;
  border-radius: 10px;
  font-size: 0.8rem;
  line-height: 1.45;
}
.nl-message--user .nl-bubble {
  background: rgb(var(--v-theme-primary));
  color: rgb(var(--v-theme-on-primary));
  border-bottom-right-radius: 3px;
}
.nl-message--assistant .nl-bubble {
  background: rgb(var(--v-theme-surface-variant));
  color: rgb(var(--v-theme-on-surface-variant));
  border-bottom-left-radius: 3px;
}

/* ── Delta preview ───────────────────────────────────────────────────── */
.nl-delta-card {
  background: rgba(var(--v-theme-primary), 0.06);
  border: 1px solid rgba(var(--v-theme-primary), 0.25);
  border-radius: 6px;
  padding: 6px 8px;
  margin-left: 4px;
}
.nl-delta-line {
  font-size: 0.75rem;
  line-height: 1.6;
  color: rgb(var(--v-theme-on-surface));
}
.nl-delta-actions {
  margin-top: 4px;
}

/* ── Slide transition ────────────────────────────────────────────────── */
.nl-slide-enter-active,
.nl-slide-leave-active {
  transition: transform 0.25s ease, opacity 0.2s ease;
}
.nl-slide-enter-from,
.nl-slide-leave-to {
  transform: translateX(100%);
  opacity: 0;
}
</style>
